import React, {useEffect, useState} from 'react';
import { ResponseResultCode } from '../../../model/response-api';
import { DbUserService } from '../../../services/db/dbUserService';
import Highcharts from 'highcharts';
import { parseNumberWithCommaDecimal } from '../../../utils/stringUtil';
import HighchartsReact from 'highcharts-react-official';
import moment from 'moment';
import {DashboardModuleProps} from "../../../model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";

const options = {
  chart: {
    type: 'column',
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
  },
  title: { text: '' },
  exporting: { enabled: false },
  credits: { enabled: false },
  legend: {
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    padding: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  plotOptions: {
    column: {
      stacking: 'normal',
      pointWidth: 20,
      borderWidth: 0,
    },
  },
  tooltip: {
    shared: true,
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;
      this.points?.forEach((point: any) => {
        tooltip += `<span style="color:${point.color}">●&nbsp;</span>${point.series.name}: <strong style="font-size: 0.75rem;">${parseNumberWithCommaDecimal(point.y)}</strong> kWh<br/>`;
      });
      return tooltip;
    },
    style: {
      fontSize: '0.75rem',
    }
  },
  xAxis: {
    categories: Array.from({length: 12}, (_, i) => `${i + 1}월`), // 1월부터 12월까지
    min: 0,
    lineColor: '#999',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
    },
  },
  yAxis: {
    title: {
      text: '(kWh)',
      align: 'high',
      rotation: 0,
      offset: 0,
      y: -25,
      style: {
        color: '#444',
        fontSize: '0.65rem',
      },
    },
    min: 0,
    softMax: 10,
    lineColor: '#999',
    lineWidth: 1,
    gridLineColor: '#CCC',
    gridLineWidth: 1,
    gridLineDashStyle: 'ShortDash',
    labels: {
      x: -5,
      y: 4,
      style: {
        color: '#444',
        fontSize: "0.7rem",
      },
      formatter: function (this: any) {
        return parseNumberWithCommaDecimal(this.value);
      },
    },
  },
  series: [],
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            spacingTop: 20,
          },
          xAxis: {
            labels: {
              style: {
                fontSize: "0.5rem",
              },
            },
          },
          yAxis: [
            {
              title: {
                y: -10,
                style: {
                  fontSize: "0.5rem",
                },
              },
              labels: {
                style: {
                  fontSize: "0.5rem",
                },
              },
            },
          ],
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemStyle: {
              fontSize: '0.6rem',
            },
          },
          tooltip: {
            style: {
              fontSize: '0.6rem',
            },
          },
          plotOptions: {
            column: {
              stacking: 'normal',
              pointWidth: undefined,
            },
          },
        },
      },
    ],
  },
}

const getColorByCategory = (category: string) => {
  switch (category) {
    case '자가발전': return '#545CC1';
    case 'REC': return '#FFBB24';
    case '녹색프리미엄': return '#9F92FF';
    case '직접 PPA': return '#4C96F9';
    case '제3자 PPA': return '#44C5DB';
    default: return '#000000'; // 기본 색상
  }
};

export default function MonthlySmcnCert(props: Readonly<DashboardModuleProps>) {
  const currentYear: string = moment().format('YYYY');
  const [chartOption, setChartOption] = useState(options);
  const [monthlyQnt, setMonthlyQnt] = useState<any>();

  useEffect(() => {
    getMonthlyQnt();
  }, []);

  async function getMonthlyQnt() {
    const response = await DbUserService.getInstance().getMonthlyQnt(currentYear);

    if (response && ResponseResultCode.Y === response.resultCode) {
      setMonthlyQnt(response.data);
    } 
  }

  useEffect(() => {
    if (monthlyQnt) {
      const categories = ['자가발전', 'REC', '녹색프리미엄', '직접 PPA', '제3자 PPA'];
      const seriesData: { name: string; data: number[] }[] = categories.map((category, index) => ({
        name: category,
        data: new Array(12).fill(0), // 12개월 초기화
        color: getColorByCategory(category),
      }));

      Object.keys(monthlyQnt).forEach((month) => {
        const monthData = monthlyQnt[month];
        Object.keys(monthData).forEach((key) => {
          const categoryIndex = parseInt(key) - 1; // 1부터 시작하므로 -1
          if (categoryIndex >= 0 && categoryIndex < seriesData.length) {
            seriesData[categoryIndex].data[parseInt(month) - 1] += monthData[key]; // 해당 월의 데이터 추가
          }
        });
      });

      setChartOption((prevState: any) => ({
        ...prevState,
        series: seriesData,
      }));
    }
  }, [monthlyQnt]);

  return (
    <DashboardCard
      title="월별 이행수단 실적"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={false}
      dashboardConfig={props.dashboardConfig}
    >
      <div className="inner-flex1">
        <div className="chart">
          <HighchartsReact highcharts={Highcharts} options={chartOption} />
        </div>
      </div>
    </DashboardCard>
  );
}